import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import Tooltip from 'react-tooltip-lite';

import { AddEditCrew } from '@/modules/workforce/Crew/components/Modals/AddEditCrew';
import { DeactivateCrewConfirmation } from '@/modules/workforce/Crew/components/Modals/DeactivateCrewConfirmation';
import { ISuppliers } from '@/modules/workforce/Crew/types';
import { Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import DotedMenu from '@/shared/components/molecules/DotedMenu';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { Item } from '@/shared/components/molecules/PartialListWithTooltip/IPartialListWithTooltip';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import ApprovedIcon from '~/images/shared/approved.svg';
import Edit from '~/images/shared/edit.svg';
import DotsMenu from '~/images/shared/HorizontalHollowDots.svg';
import QRCode from '~/images/shared/scan-barcode-filled.svg';
import Trash from '~/images/shared/TrashWithoutColor.svg';

const columnHelper = createColumnHelper<ISuppliers.ISupplier>();

const useCrewHook = () => {
  const [isDemoFarm] = useStore((store) => store.isDemoFarm);
  const { closeModal, openModal } = useModal();

  const suppliersTableColumns = (
    t: TFunction<'common', undefined>,
    onClickQrHandler: (row: ISuppliers.ISupplier) => void
  ) => [
    columnHelper.accessor('supplierName', {
      cell: ({ getValue, row }) => (
        <div className='flex w-full items-center gap-5 '>
          <Typography
            variant='subtitle2'
            className=' overflow-clip text-ellipsis whitespace-nowrap'
          >
            {getValue()}
          </Typography>
          {row?.original?.supplierApproved === 1 && (
            <Tooltip
              arrow
              arrowSize={8}
              content={
                <Typography variant='body2'>
                  {t('team-leader-approved')}
                </Typography>
              }
              direction='down'
              tagName='div'
              background='#DADDDF'
              distance={15}
            >
              <span>
                <ApprovedIcon />
              </span>
            </Tooltip>
          )}
        </div>
      ),
      header: () => <Typography variant='subtitle1'>{t('name')}</Typography>,
    }),
    columnHelper.accessor('phoneNumber', {
      cell: ({ getValue, row }) => (
        <div className='flex w-full items-center justify-start gap-5'>
          <Typography
            variant='subtitle2'
            className=' overflow-clip text-ellipsis whitespace-nowrap '
          >
            {getValue() && getValue().length > 0 ? (
              `\u202A${getValue()}`
            ) : (
              <span className='text-24'>-</span>
            )}
          </Typography>
          {row?.original?.mobileNumberVerified === 1 && (
            <Tooltip
              arrow
              arrowSize={8}
              content={
                <Typography variant='body2' className='whitespace-nowrap'>
                  {t('mobile-number-approved')}
                </Typography>
              }
              direction='down'
              tagName='div'
              background='#DADDDF'
              distance={15}
            >
              <span>
                <ApprovedIcon />
              </span>
            </Tooltip>
          )}
        </div>
      ),
      header: () => t('phone-number'),
    }),
    columnHelper.accessor('jobs', {
      cell: ({ getValue }) =>
        getValue()?.length !== 0 ? (
          <PartialListWithTooltip
            limit={1}
            items={getValue().map((job) => {
              return {
                description: job.jobName,
                imageURL: job.cropImage,
              } as Item;
            })}
          />
        ) : (
          <span className='text-24'>-</span>
        ),
      header: () => t('experience'),
    }),
    columnHelper.accessor('workersCount', {
      cell: ({ getValue }) => (
        <span className='flex w-full items-center gap-5'>{`${getValue()} ${t(
          'individual'
        )}`}</span>
      ),
      header: () => t('Crew'),
    }),
    columnHelper.accessor('qrCode', {
      cell: ({ row: { original } }) => (
        <div className='flex items-center justify-end gap-24'>
          <div
            onClick={(e) => {
              e.stopPropagation();
              onClickQrHandler(original);
            }}
            className='font-semibold cursor-pointer items-center justify-center gap-1'
          >
            <BaseIcon
              svgClasses='cursor-pointer text-primary-default'
              size='md'
            >
              <QRCode />
            </BaseIcon>
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
              openModal(
                <AddEditCrew
                  crewId={original.supplierId}
                  initialValues={{
                    name: original.supplierName,
                    phoneNumber: original.phoneNumber,
                    countryCode: original.countryCode,
                  }}
                />,
                {
                  variant: 'full',
                  title: t('edit-crew'),
                  titleVariant: 'h6',
                  onClose: () =>
                    openModal(
                      <ConfirmationModal
                        header={t('going-somewhere-else')}
                        message={t('you-will-lose-all-data')}
                        variant='warning'
                        dismissText={t('continue-editing')}
                        confirmText={t('discard-and-exit')}
                        onDismissRemoveNumber={1}
                        onConfirm={closeModal}
                        buttonsAlignment='col'
                        preferredAction='dismiss'
                      />
                    ),
                }
              );
            }}
            className='font-semibold cursor-pointer items-center justify-center gap-1'
          >
            <BaseIcon
              svgClasses='cursor-pointer text-primary-default'
              size='md'
            >
              <Edit />
            </BaseIcon>
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className='font-semibold cursor-pointer items-center justify-center gap-1'
          >
            <DotedMenu
              list={[
                {
                  title: (
                    <div className='flex items-center gap-2 text-error-400'>
                      <Trash width='24' height='24' />
                      <span>{t('deactivate')}</span>
                    </div>
                  ),
                  id: '0',
                  action: () =>
                    isDemoFarm
                      ? openModal(<DemoCTA />)
                      : openModal(
                          <DeactivateCrewConfirmation
                            crewId={original.supplierId}
                          />,
                          {
                            containerClassName: 'max-w-md',
                          }
                        ),
                },
              ]}
              getTitle={(item) => item?.title}
              getId={(item) => item?.id}
              getAction={(item) => item.action}
              onOpen={() => {
                //
              }}
              DotsMenuIcon={<DotsMenu className='text-24' />}
            />
          </div>
        </div>
      ),
      header: () => '',
    }),
  ];

  return {
    suppliersTableColumns,
  };
};
export default useCrewHook;
